import React, { useState } from 'react';
import {
  Box, Typography, Pagination, Paper,
} from '@mui/material';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Design tokens matching MSIS
const C = {
  navy:       '#273F77',
  orange:     '#E79A2B',
  bg:         '#f5f6fa',
  cardBg:     '#ffffff',
  border:     '#e8eaf0',
  textMuted:  '#8492a6',
};

const PAGE_SIZE = 6;

// ─── Attempt status badge ─────────────────────────────────────────────────────
const AttemptBadge = ({ status }) => {
  if (!status) return null;

  const { passed, attempt_status, attempts_made, attempts_remaining } = status;

  // Not attempted yet
  if (attempt_status === 'not started' || attempts_made === 0) {
    return (
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        px: 1.25, py: 0.4, borderRadius: '20px',
        backgroundColor: 'rgba(132,146,166,0.12)',
      }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: C.textMuted }} />
        <Typography variant="caption" fontWeight={700} color={C.textMuted}>Not Attempted</Typography>
      </Box>
    );
  }

  // In progress
  if (attempt_status === 'in progress') {
    return (
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        px: 1.25, py: 0.4, borderRadius: '20px',
        backgroundColor: 'rgba(231,154,43,0.12)',
      }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: C.orange }} />
        <Typography variant="caption" fontWeight={700} color={C.orange}>In Progress</Typography>
      </Box>
    );
  }

  // Completed — show pass/fail
  if (passed) {
    return (
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        px: 1.25, py: 0.4, borderRadius: '20px',
        backgroundColor: 'rgba(46,125,50,0.1)',
      }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 12, color: '#2e7d32' }} />
        <Typography variant="caption" fontWeight={700} color="#2e7d32">Passed</Typography>
        {attempts_remaining > 0 && (
          <Typography variant="caption" color="#2e7d32" sx={{ opacity: 0.7 }}>
            · {attempts_remaining} left
          </Typography>
        )}
      </Box>
    );
  }

  // Failed
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.25, py: 0.4, borderRadius: '20px',
      backgroundColor: attempts_remaining === 0 ? 'rgba(198,40,40,0.08)' : 'rgba(198,40,40,0.08)',
    }}>
      <CancelOutlinedIcon sx={{ fontSize: 12, color: '#c62828' }} />
      <Typography variant="caption" fontWeight={700} color="#c62828">
        {attempts_remaining === 0 ? 'Failed · No attempts left' : `Failed · ${attempts_remaining} attempt${attempts_remaining !== 1 ? 's' : ''} left`}
      </Typography>
    </Box>
  );
};

const QuizCard = ({ quiz, index, page, onSelect, attemptStatus }) => (
  <Paper
    onClick={() => onSelect(quiz)}
    elevation={0}
    sx={{
      borderRadius: '16px',
      border: `1.5px solid`,
      borderColor: attemptStatus?.passed ? 'rgba(46,125,50,0.25)'
        : attemptStatus?.attempt_status === 'completed' && !attemptStatus?.passed ? 'rgba(198,40,40,0.2)'
        : C.border,
      p: 2.5,
      mb: 2,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      backgroundColor: attemptStatus?.passed ? 'rgba(46,125,50,0.02)'
        : attemptStatus?.attempt_status === 'completed' && !attemptStatus?.passed ? 'rgba(198,40,40,0.02)'
        : C.cardBg,
      transition: 'box-shadow 0.18s, transform 0.18s',
      '&:hover': {
        boxShadow: '0 6px 24px rgba(39,63,119,0.10)',
        transform: 'translateY(-2px)',
      },
    }}
  >
    {/* Number / result icon */}
    <Box sx={{
      width: 48, height: 48, borderRadius: '12px',
      backgroundColor: attemptStatus?.passed ? 'rgba(46,125,50,0.1)'
        : attemptStatus?.attempt_status === 'completed' && !attemptStatus?.passed ? 'rgba(198,40,40,0.08)'
        : 'rgba(39,63,119,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {attemptStatus?.passed
        ? <CheckCircleOutlineIcon sx={{ fontSize: 24, color: '#2e7d32' }} />
        : attemptStatus?.attempt_status === 'completed'
          ? <CancelOutlinedIcon sx={{ fontSize: 24, color: '#c62828' }} />
          : <Typography fontWeight={800} color={C.navy} fontSize="1.1rem">
              {(page - 1) * PAGE_SIZE + index + 1}
            </Typography>
      }
    </Box>

    {/* Info */}
    <Box flex={1} minWidth={0}>
      <Box display="flex" alignItems="center" gap={1} mb={0.75} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={700} color={C.navy}>
          {quiz.title}
        </Typography>
        {/* Active/Inactive pill */}
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.25, borderRadius: '20px',
          backgroundColor: quiz.isActive ? 'rgba(46,125,50,0.1)' : '#f5f5f5',
        }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            backgroundColor: quiz.isActive ? '#2e7d32' : '#9e9e9e',
          }} />
          <Typography variant="caption" fontWeight={700}
            color={quiz.isActive ? '#2e7d32' : '#9e9e9e'} lineHeight={1}>
            {quiz.isActive ? 'Active' : 'Inactive'}
          </Typography>
        </Box>
        {/* Attempt status badge */}
        <AttemptBadge status={attemptStatus} />
      </Box>

      {/* Meta row */}
      <Box display="flex" gap={2.5} flexWrap="wrap">
        {[
          { icon: <QuizOutlinedIcon sx={{ fontSize: 13 }} />, text: `${quiz.total_questions} Questions` },
          { icon: <AccessTimeIcon sx={{ fontSize: 13 }} />, text: `${quiz.estimated_time_req} min` },
          { icon: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />, text: `Pass: ${quiz.passing_score}%` },
          { icon: <RepeatIcon sx={{ fontSize: 13 }} />, text: `${quiz.attempts_allowed} attempt${quiz.attempts_allowed !== 1 ? 's' : ''}` },
        ].map(({ icon, text }) => (
          <Box key={text} display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ color: C.textMuted }}>{icon}</Box>
            <Typography variant="caption" color={C.textMuted} fontWeight={500}>{text}</Typography>
          </Box>
        ))}
        {/* Score if attempted */}
        {attemptStatus?.attempts_made > 0 && attemptStatus?.score !== null && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ color: attemptStatus.passed ? '#2e7d32' : '#c62828' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />
            </Box>
            <Typography variant="caption" fontWeight={700}
              color={attemptStatus.passed ? '#2e7d32' : '#c62828'}>
              Last score: {attemptStatus.score}/{quiz.total_questions}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>

    <ChevronRightIcon sx={{ color: C.border, flexShrink: 0 }} />
  </Paper>
);

const QuizList = ({ quizzes, moduleName, onSelect, quizStatuses = {} }) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(quizzes.length / PAGE_SIZE);
  const paginated  = quizzes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const passedCount    = quizzes.filter((q) => quizStatuses[q.id]?.passed).length;
  const attemptedCount = quizzes.filter((q) => quizStatuses[q.id]?.attempts_made > 0).length;

  return (
    <Box maxWidth={760} mx="auto">
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight={800} color={C.navy} mb={0.5}>
          Quizzes
        </Typography>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Typography variant="body2" color={C.textMuted}>
            Module: <strong style={{ color: C.navy }}>{moduleName}</strong>
            &nbsp;·&nbsp;{quizzes.length} quiz{quizzes.length !== 1 ? 'zes' : ''}
          </Typography>
          {attemptedCount > 0 && (
            <>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: C.border }} />
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 1.25, py: 0.3, borderRadius: '20px',
                backgroundColor: 'rgba(46,125,50,0.1)',
              }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 12, color: '#2e7d32' }} />
                <Typography variant="caption" fontWeight={700} color="#2e7d32">
                  {passedCount} passed
                </Typography>
              </Box>
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 1.25, py: 0.3, borderRadius: '20px',
                backgroundColor: 'rgba(39,63,119,0.08)',
              }}>
                <Typography variant="caption" fontWeight={700} color={C.navy}>
                  {attemptedCount} attempted
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {paginated.map((quiz, idx) => (
        <QuizCard
          key={quiz.id}
          quiz={quiz}
          index={idx}
          page={page}
          onSelect={onSelect}
          attemptStatus={quizStatuses[quiz.id]}
        />
      ))}

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, val) => { setPage(val); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': { color: C.navy, fontWeight: 600, borderRadius: '8px' },
              '& .Mui-selected': { backgroundColor: `${C.navy} !important`, color: '#fff' },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default QuizList;